import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useModalConfirm,
  usePageContext,
  usePassengersBulkActions,
  useSelectedIds,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  ConfirmResult,
  DrawerType,
  useDrawers,
  useSystemParams,
} from "@ryde/shared";

import { BulkPassengerNearbySearchFormFieldsEnum } from "../models";
import { useBulkPassengerNearbySearchFormValidation } from "./use-bulk-passenger-nearby-form.validation";

export interface IBulkPassengerNearbySearchForm {
  distanceInMeters: number;
}

export const useBulkPassengerNearbySearch = () => {
  const { entity } = usePageContext();
  const { selectedRowsIds } = useSelectedIds("passenger");
  const { passengerStationNearByDistanceMeters } = useSystemParams();
  const { handleBulkFindNearbyStation } = usePassengersBulkActions();
  const { bulkPassengerNearbySearchFormValidation } =
    useBulkPassengerNearbySearchFormValidation();

  const { confirmDialogResult } = useModalConfirm();
  const { closeDrawer } = useDrawers();

  const { t } = useTranslation();

  const resolver = useYupValidationResolver(
    bulkPassengerNearbySearchFormValidation,
    entity,
  );

  const methods = useForm<IBulkPassengerNearbySearchForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver,
    defaultValues: {
      distanceInMeters: undefined,
    },
  });

  const { getValues, trigger } = methods;

  const handleSearchBulkPassengerNearbyStation = useEvent(async () => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.BULK_UPDATE_NEARBY_STATION_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const distanceInMetersField = getValues(
        BulkPassengerNearbySearchFormFieldsEnum.distanceInMeters,
      );

      const isUpdateSuccess = await handleBulkFindNearbyStation({
        ids: selectedRowsIds,
        distanceInMeters:
          distanceInMetersField ?? passengerStationNearByDistanceMeters,
        isAllowBusStationSearch: false,
        isSetOnlyForMissingPickup: false,
      });

      if (isUpdateSuccess) {
        closeDrawer(DrawerType.BulkPassengerNearBySearch);
      }
    }
  });

  return {
    methods,
    handleSearchBulkPassengerNearbyStation,
  };
};
